import type { components } from '@/lib/api/generated/schema';
import { apiGet, apiPost } from '@/lib/api/client';

type Schemas = components['schemas'];

export type LanguageDto = Schemas['LanguageOut'];

export type ShopDto = Schemas['ShopOut'];

export type CategoryDto = Schemas['CategoryOut'];

export type DishSummaryDto = Schemas['DishSummaryOut'];

export type DishDetailDto = Schemas['DishDetailOut'];

export type MenuDto = Schemas['MenuOut'];

export function fetchShop(slug: string, signal?: AbortSignal): Promise<ShopDto> {
  return apiGet<ShopDto>(`/public/shops/${encodeURIComponent(slug)}`, signal);
}

export function fetchMenu(
  slug: string,
  language: string,
  signal?: AbortSignal,
): Promise<MenuDto> {
  const query = language ? `?lang=${encodeURIComponent(language)}` : '';
  return apiGet<MenuDto>(`/public/shops/${encodeURIComponent(slug)}/menu${query}`, signal);
}

export function fetchDish(
  slug: string,
  dishId: string,
  language: string,
  signal?: AbortSignal,
): Promise<DishDetailDto> {
  const query = language ? `?lang=${encodeURIComponent(language)}` : '';
  return apiGet<DishDetailDto>(
    `/public/shops/${encodeURIComponent(slug)}/dishes/${encodeURIComponent(dishId)}${query}`,
    signal,
  );
}

export function formatPrice(
  amount: string | null | undefined,
  currency: string | null | undefined,
  taxIncluded: boolean | null | undefined,
  taxIncludedLabel = 'incl. tax',
  taxExcludedLabel = 'excl. tax',
): string | null {
  if (amount === null || amount === undefined) return null;
  if (currency === null || currency === undefined) return null;

  const numeric = Number(amount);
  const formatted = Number.isFinite(numeric)
    ? numeric.toFixed(2)
    : amount;

  const taxNote = taxIncluded === null || taxIncluded === undefined
    ? ''
    : taxIncluded
      ? ` ${taxIncludedLabel}`
      : ` ${taxExcludedLabel}`;

  const SYMBOLS: Record<string, string> = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
   };

  const symbol = SYMBOLS[currency.toUpperCase()];
  const body = symbol ? `${symbol}${formatted}` : `${currency} ${formatted}`;
  return `${body}${taxNote}`;
}

export type QaAnswer = Schemas['QuestionOut'];

export function askQuestion(
  shopSlug: string,
  question: string,
  languageCode: string,
  requestId: string,
): Promise<QaAnswer> {
  return apiPost<QaAnswer>(`/public/shops/${shopSlug}/questions`, {
    question,
    language_code: languageCode,
    request_id: requestId,
  });
}